import { Component, inject, OnDestroy } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom, Subscription } from 'rxjs';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { ActionColor, CheckBoxAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { QuotationDetailModel } from 'src/app/models/preliminaries/quotation-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { CompositeArticleDetailSelectTableModel } from 'src/app/models/tables/preliminaries/composite-article-select-detail-table.model';
import { CompositeArticleSelectTableModel } from 'src/app/models/tables/preliminaries/composite-article-select-table.model';
import { BreakdownService } from 'src/app/services/preliminaries/breakdown.service';
import { CompositeArticleDetailService } from 'src/app/services/preliminaries/composite-article-detail.service';
import { CompositeArticleService } from 'src/app/services/preliminaries/composite-article.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-detail-from-composite-article',
  templateUrl: './detail-from-composite-article.component.html',
  styleUrl: './detail-from-composite-article.component.scss',
  standalone: false
})
export class DetailFromCompositeArticleComponent implements OnDestroy {

  private readonly breakdownService = inject(BreakdownService);
  private readonly compositeArticleDetailService = inject(CompositeArticleDetailService);
  private readonly compositeArticleService = inject(CompositeArticleService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly config = inject(DynamicDialogConfig);
  private readonly ref = inject(DynamicDialogRef, { optional: true });

  ascendant: boolean = false;
  ascendantDetail: boolean = false;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Agregar',
      icon: 'pi pi-plus-circle',
      color: ActionColor.SUCCESS,
      action: 'add'
    },
  ];
  columnSort: string | null = null;
  columnSortDetail: string | null = null;
  compositeArticleId: number = 0;
  currentStep: number = 1;
  data: PagedResponseModel<CompositeArticleSelectTableModel> =
    new PagedResponseModel<CompositeArticleSelectTableModel>();
  dataDetail: PagedResponseModel<CompositeArticleDetailSelectTableModel> =
    new PagedResponseModel<CompositeArticleDetailSelectTableModel>();
  filters: ColumnResponseModel[] = [];
  filtersDetail: ColumnResponseModel[] = [];
  headers: ColumnResponseModel[] = [];
  headersDetail: ColumnResponseModel[] = [];
  importing: boolean = false;
  limit: number = 10;
  limitDetail: number = 10;
  loading: boolean = false;
  page: number = 1;
  pageDetail: number = 1;
  quotationSolutionModel: QuotationDetailModel = new QuotationDetailModel();
  quotationVersionId: number = 0;
  readonly stepPanel = StepPanelEnums;
  subscription: Subscription;

  async ngOnInit() {
    this.subscription = this.config.data?.inputs$?.subscribe((inputs: any) => {
      this.quotationSolutionModel = inputs.quotationSolutionModel;
      this.quotationVersionId = inputs.quotationVersionId;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async createBreakdownFromCompositeArticle(selectedItems: CompositeArticleDetailSelectTableModel[]) {
    if (this.importing) return;
    this.importing = true;
    try {
      const response = await firstValueFrom(
        this.breakdownService.createBreakdownFromMasterSolutionAsync(
          this.quotationVersionId, this.quotationSolutionModel.quotationDetailId, selectedItems[0].compositeArticleId,
          selectedItems.map(item => item.compositeArticleDetailId)
        )
      );
      this.toastHandlerService.showToast('success', 'Se procesó el artículo compuesto exitosamente.');
      this.config.data?.output$?.next({ type: 'quotationDetailId', payload: response.quotationDetailId });
      this.ref?.close();
    } catch (error) {
      console.error('Error al procesar el artículo compuesto.', error);
    } finally {
      this.importing = false;
    }
  }

  async getColumnsCompositeArticleSelection() {
    try {
      if (this.headers.length === 0) {
        const response = await firstValueFrom(
          this.compositeArticleService.getColumnsCompositeArticleSelection()
        );
        this.headers = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta.', error);
    }
  }

  async getColumnsForDetailSelection() {
    try {
      if (this.headersDetail.length === 0) {
        const response = await firstValueFrom(
          this.compositeArticleDetailService.getColumnsForDetailSelection()
        );
        this.headersDetail = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta detalle.', error);
    }
  }

  async getCompositeArticlesDetailsForSelection() {
    try {
      const response = await firstValueFrom(
        this.compositeArticleDetailService.getCompositeArticlesDetailsForSelection(
          this.pageDetail, this.limitDetail, this.columnSortDetail, this.ascendantDetail,
          this.filtersDetail, this.compositeArticleId ?? 0
        )
      );
      this.dataDetail = response;
    } catch (error) {
      console.error('Error al obtener los detalles de artículos compuesto.', error);
    }
  }

  async getCompositeArticlesForSelection() {
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.getCompositeArticlesForSelection(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters
        )
      );
      this.data = response;
    } catch (error) {
      console.error('Error al obtener la lista de artículos compuesto.', error);
    }
  }

  async getCompositeArticleById(compositeArticleId: number) {
    try {
      const response = await firstValueFrom(
        this.compositeArticleService.getCompositeArticleById(compositeArticleId)
      );
      this.compositeArticleId = response.articleId;
    } catch (error) {
      console.error('Error al obtener el artículo compuesto.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsCompositeArticleSelection(),
          this.getCompositeArticlesForSelection()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsForDetailSelection(),
          this.getCompositeArticlesDetailsForSelection()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step > StepPanelEnums.PanelOne && this.compositeArticleId === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Por favor, seleccione un artículo compuesto válido.'
      );
      return;
    }
    if (step === StepPanelEnums.PanelOne) {
      this.compositeArticleId = 0;
    }
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }


  async onAction(event: { action: string, item?: CompositeArticleDetailSelectTableModel[] }) {
    if (event.action === 'add' && event.item?.length) {
      await this.createBreakdownFromCompositeArticle(event.item);
    }
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filters = cleanFilters;
        this.filters.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersDetail = cleanFilters;
        this.filtersDetail.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.page = event.page;
        this.limit = event.pageSize;
        this.columnSort = event.sortField || null;
        this.ascendant = event.sortOrder === 1;
        await this.getCompositeArticlesForSelection()
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.pageDetail = event.page;
        this.limitDetail = event.pageSize;
        this.columnSortDetail = event.sortField || null;
        this.ascendantDetail = event.sortOrder === 1;
        await this.getCompositeArticlesDetailsForSelection()
        break;
      }
    }
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filters = this.filters.filter(f => f.propertyName !== propertyName);
      } else if (step == StepPanelEnums.PanelTwo) {
        this.filtersDetail = this.filtersDetail.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step === StepPanelEnums.PanelOne ? this.filters : this.filtersDetail;
      const existingFilter = list.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async selectionChange(event: { selectedItem?: any }) {
    const selectedId = event?.selectedItem?.id;
    if (!selectedId) {
      this.compositeArticleId = 0;
      return;
    }

    if (this.currentStep === StepPanelEnums.PanelOne) {
      await this.getCompositeArticleById(selectedId);
    }
  }

}
